import { z } from 'zod/v4';

/**
 * A nested blob carried through verbatim.
 *
 * `looseObject({})` rather than a declared shape, and that is deliberate: across
 * the ~913 datapoints this catalogue returns, `meta`, `schema` and
 * `externalIds[]` are genuinely heterogeneous — `meta` is anything from
 * `{unit}` to a 14-key `slcpMeta` block. Declaring those shapes would mean every
 * key optional, and no validation strength is gained by it.
 *
 * `options[]` used to be one of these. It is declared below instead: the keys
 * consumers read by name are the same in every consumer, and leaving them
 * undescribed meant each one wrote its own view of them —
 * which is how `followUp` reached the responder in two dashboards and neither
 * handled the one option that wraps it in an array.
 */
const PassthroughBlobSchema = z.looseObject({});

/**
 * The second question an option asks once it is picked — "If other, please
 * describe:".
 *
 * `fieldType` is `SHORT_TEXT`, `NUMBER`, `SELECT` or `YES_NO` today, and stays
 * `z.string()` for the same reason `fieldTypeId` does: the value domain is
 * open, and a new one must round-trip rather than fail the whole catalogue.
 */
export const QuestionnaireDataPointOptionFollowUpSchema = z.looseObject({
  text: z.string().nullable().optional(),
  fieldType: z.string().nullable().optional(),
});

export type QuestionnaireDataPointOptionFollowUp = z.infer<
  typeof QuestionnaireDataPointOptionFollowUpSchema
>;

/**
 * One allowed value of a choice datapoint.
 *
 * `looseObject`, so the keys not declared here still ride along — `slcpIndex`,
 * `slcpOptionName`, `externalIds`, and the per-column defaults the seven TABLE
 * datapoints hang off their options. Only what a consumer reads by name is
 * declared.
 *
 * The two unions are measured, not defensive. Across the 2305 options the
 * catalogue returns:
 *
 * - `name` is a **number** on seven of them (the ages 12–18 of
 *   `what_is_the_age_of_the_youngest_worker_in_the_fac`), so `z.string()` alone
 *   would 500 the whole catalogue.
 * - `followUp` is an object on 232 and a one-element **array** on one
 *   (`does_the_fac_engage_proactively_w_relevant_stakeholders_before`). Read
 *   `.text` straight off it and that option silently asks nothing, which is
 *   what both dashboards did — use {@link dataPointOptionFollowUp}.
 */
export const QuestionnaireDataPointOptionSchema = z.looseObject({
  key: z.string().nullable().optional(),
  name: z.union([z.string(), z.number()]).nullable().optional(),
  text: z.string().nullable().optional(),
  followUp: z
    .union([
      QuestionnaireDataPointOptionFollowUpSchema,
      z.array(QuestionnaireDataPointOptionFollowUpSchema),
    ])
    .nullable()
    .optional(),
});

export type QuestionnaireDataPointOption = z.infer<
  typeof QuestionnaireDataPointOptionSchema
>;

/**
 * An option's follow-up prompt, whichever way the library wrapped it.
 *
 * One datapoint stores it as a one-element array rather than an object. Every
 * consumer that reads `option.followUp.text` directly gets `undefined` there and
 * renders no prompt, so the supplier is never asked the question the standard
 * requires — a silent gap, since nothing errors. Reading it through here is what
 * keeps that from being rediscovered per consumer.
 */
export const dataPointOptionFollowUp = (
  option: QuestionnaireDataPointOption | undefined,
): QuestionnaireDataPointOptionFollowUp | undefined => {
  const followUp = option?.followUp;
  if (followUp === null || followUp === undefined) return undefined;
  const resolved = Array.isArray(followUp) ? followUp[0] : followUp;
  return resolved?.text ? resolved : undefined;
};

/**
 * An option's display label, as a string.
 *
 * `name` is a number on the age options, so a consumer that assigns it straight
 * to a `string` field is lying to the compiler about what it holds.
 */
export const dataPointOptionLabel = (
  option: QuestionnaireDataPointOption | undefined,
): string | undefined => {
  const name = option?.name;
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof name === 'number') return String(name);
  const text = option?.text;
  return typeof text === 'string' && text.length > 0 ? text : undefined;
};

/**
 * One datapoint definition, exactly as `@retracedgmbh/data-points-lib` exposes it
 * under `getDataPointById(id).dataPointDefinition`.
 *
 * `looseObject` is **load-bearing**, not stylistic. The legacy response schema was
 * `{type: 'array', items: {type: 'object', additionalProperties: true}}`, so the
 * whole definition reached the wire unfiltered. The zod serializer strips
 * undeclared keys, and `frontend/src/hooks/useBuilderSider.js` spreads
 * `...otherProps` from every datapoint straight into question metadata with no
 * allow-list — so a key dropped here does not error anywhere, it just builds a
 * question with the wrong answer type. The declared keys below are the ones a
 * frontend reads by name; everything else rides along.
 *
 * Note there is **no `id`** here. The definitions carry `retracedId` only; `id` is
 * added exclusively on the grouped path (see
 * `QuestionnaireDataPointTemplateGroupItemSchema`). That asymmetry is legacy's and
 * is preserved — `frontend/src/views/audits/hooks/useSyncDigitizedAudit.ts` reads
 * `dataPoint.id` off the flat list and has therefore always sent `undefined`. That
 * is a pre-existing frontend bug, not something to paper over by widening the wire.
 */
export const QuestionnaireDataPointSchema = z.looseObject({
  /** The datapoint's stable id — the value `composed_of` stores. */
  retracedId: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  /** Answer-type key (`SELECT`, `NUMBER_UOM`, `SHORT_TEXT`, …). */
  fieldTypeId: z.string().nullable().optional(),
  /** `GLOBAL` | `RELATIONAL` | `TRANSACTIONAL`. frontend-v2 branches on `GLOBAL`. */
  type: z.string().nullable().optional(),
  impactArea: z.string().nullable().optional(),
  impactAreaId: z.string().nullable().optional(),
  sectionId: z.string().nullable().optional(),
  sortingId: z.number().nullable().optional(),
  options: z.array(QuestionnaireDataPointOptionSchema).optional(),
  externalIds: z.array(PassthroughBlobSchema).optional(),
  meta: PassthroughBlobSchema.nullable().optional(),
  schema: PassthroughBlobSchema.nullable().optional(),
});

export type QuestionnaireDataPoint = z.infer<
  typeof QuestionnaireDataPointSchema
>;

/**
 * Response of `GET /api/questionnaires/data-points`.
 *
 * A bare array under the `apiSuccess` `data` key. Unpaginated, as legacy was.
 */
export const QuestionnaireDataPointListSchema = z.array(
  QuestionnaireDataPointSchema,
);

export type QuestionnaireDataPointList = z.infer<
  typeof QuestionnaireDataPointListSchema
>;

/**
 * A datapoint as it appears **inside a template group**: the same definition plus
 * `id`, aliased to `retracedId`.
 *
 * The legacy grouped reducer spread `{...dataPoint, id: dataPoint.retracedId}`.
 * Both frontends' builder sidebars key their tree nodes off that `id`
 * (`useBuilderSider.js` destructures it; frontend-v2's `questionLibrary.ts` reads
 * it), so it is part of the contract rather than redundant with `retracedId`.
 */
export const QuestionnaireDataPointTemplateGroupItemSchema =
  QuestionnaireDataPointSchema.extend({
    id: z.string(),
  });

export type QuestionnaireDataPointTemplateGroupItem = z.infer<
  typeof QuestionnaireDataPointTemplateGroupItemSchema
>;

/**
 * One template's worth of datapoints.
 *
 * `category` is the template id, not a display label — the legacy reducer set
 * `category: template.id`, and both builder sidebars match tree nodes on it. The
 * display string is `name`.
 *
 * `dataPoints` may be **empty**: the group list iterates every row of
 * `companies_service_templates`, while the datapoints themselves come from the
 * already-filtered flat list, so a template composed entirely of `audit_*` or
 * deny-listed ids still appears with nothing in it. Legacy behaved this way and
 * the frontends tolerate it (an empty branch in the tree).
 */
export const QuestionnaireDataPointTemplateGroupSchema = z.object({
  category: z.string(),
  name: z.string().nullable().optional(),
  dataPoints: z.array(QuestionnaireDataPointTemplateGroupItemSchema),
});

export type QuestionnaireDataPointTemplateGroup = z.infer<
  typeof QuestionnaireDataPointTemplateGroupSchema
>;

/**
 * Response of `GET /api/questionnaires/data-points/templates`.
 *
 * Replaces the legacy `?groupByTemplates=true` branch of the same URL. The flag is
 * gone: one URL served two unrelated shapes, and every frontend wrapper already
 * committed to one of them, so each shape now gets its own route and its own
 * precise schema.
 */
export const QuestionnaireDataPointTemplateGroupListSchema = z.array(
  QuestionnaireDataPointTemplateGroupSchema,
);

export type QuestionnaireDataPointTemplateGroupList = z.infer<
  typeof QuestionnaireDataPointTemplateGroupListSchema
>;
